import React from "react";

const Star: React.FC<{
  filled?: boolean;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}> = ({ filled = false, size = 24, className, style }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={filled ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={style}
    aria-hidden="true"
  >
    <path d="M12 2l2.81 6.63L22 9.24l-5.46 4.73L17.82 21 12 17.27 6.18 21l1.28-7.03L2 9.24l7.19-.61L12 2z" />
  </svg>
);

// StarRating: only change is the animation-delay to include --start-delay
export const StarRating: React.FC<{
  value: number;
  size?: number;
  gap?: number;
  color?: string;
  emptyColor?: string;
  animate?: boolean;
  popMs?: number;
  staggerMs?: number;
  banner?: boolean; // simple on/off
}> = ({
  value,
  size = 28,
  gap = 8,
  color = "#ffd166",
  emptyColor = "#666",
  animate = true,
  popMs = 800,
  staggerMs = 120,
  banner = true,
}) => {
  const stars = Array.from({ length: 5 }, (_, i) => i < value);

  return (
    <div
      role="img"
      aria-label={`${value} out of 5 stars`}
      className={`star-rating${banner ? " with-banner" : ""}`}
      style={
        {
          display: "inline-flex",
          gap: `${gap}px`,
          color,
          ["--pop-ms"]: `${popMs}ms`,
          ["--stagger-ms"]: `${staggerMs}ms`,
          ["--empty-color"]: emptyColor,
        } as React.CSSProperties
      }
    >
      {stars.map((filled, i) => (
        <Star
          key={i}
          filled={filled}
          size={size}
          className={animate && filled ? "star-pop" : undefined}
          style={
            animate && filled
              ? ({ ["--i"]: i } as React.CSSProperties)
              : undefined
          }
        />
      ))}

      <style>{`
        .star-rating.with-banner {
          padding: 10px 16px;
          border-radius: 12px;
          background: linear-gradient(180deg, #fafafa, #e0e0e0);
          border: 1px solid rgba(255,255,255,0.12);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
        }

        .star-rating svg[fill="none"] {
          color: var(--empty-color);
        }

        .star-pop {
          transform-origin: center;
          animation: starPop var(--pop-ms) cubic-bezier(.2,.9,.15,1.1) both;
          animation-delay: calc(var(--stagger-ms) * var(--i));
        }

        @keyframes starPop {
          0%   { opacity: 0; transform: scale(0.45) rotate(-18deg); }
          55%  { opacity: 1; transform: scale(1.3) rotate(4deg); }
          75%  { transform: scale(0.95) rotate(0deg); }
          100% { transform: scale(1) rotate(0deg); }
        }
      `}</style>
    </div>
  );
};

// Overlay: add delayMs and wire it into --start-delay. Also fade-in the whole block.
// Overlay: now uses `slideTo` (absolute target Y from the top).
export const StarRatingOverlay: React.FC<{
  value: number;
  show?: boolean;
  size?: number;
  color?: string;
  emptyColor?: string;
  gap?: number;
  holdMs?: number;
  slideMs?: number;
  slideTo?: string; // <--- NEW: absolute target Y from top
  popMs?: number;
  staggerMs?: number;
  delayMs?: number;
  animate?: boolean;
}> = ({
  value,
  show = true,
  size,
  color,
  emptyColor,
  gap,
  holdMs = 900,
  slideMs = 700,
  slideTo = "calc(var(--safe-top, 0px) + 75px)", // <--- default target
  popMs,
  staggerMs,
  delayMs = 0,
  animate = true,
}) => {
  if (!show) return null;

  // If not animating, force the “post-animation” state:
  const floaterStyle: React.CSSProperties = animate
    ? {}
    : {
        opacity: 1,
        transform: "translateY(calc(-1 * var(--slide-distance)))",
        animation: "none",
      };

  return (
    <div
      className="rating-overlay"
      style={
        {
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
          ["--start-delay"]: `${animate ? delayMs : 0}ms`,
          ["--hold-ms"]: `${animate ? holdMs : 0}ms`,
          ["--slide-ms"]: `${animate ? slideMs : 0}ms`,
          // Distance to travel from vertical center (50vh) to the target Y:
          ["--slide-to"]: slideTo,
          ["--slide-distance"]: "calc(50vh - var(--slide-to))",
        } as React.CSSProperties
      }
    >
      <div className="rating-floater" style={floaterStyle}>
        <StarRating
          value={value}
          size={size}
          color={color}
          emptyColor={emptyColor}
          gap={gap}
          animate={animate}
          popMs={popMs}
          staggerMs={staggerMs}
        />
      </div>

      <style>{`
        .rating-floater {
          opacity: 0;
          transform: translateY(0);
          animation:
            fadeIn 200ms ease-out var(--start-delay) both,
            slideUp var(--slide-ms) cubic-bezier(.2,.9,.2,1)
              calc(var(--start-delay) + var(--hold-ms)) forwards;
          will-change: opacity, transform;
        }
        @keyframes slideUp {
          from { transform: translateY(0); }
          to   { transform: translateY(calc(-1 * var(--slide-distance))); }
        }
        @keyframes fadeIn { to { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) {
          .rating-floater { animation: none !important; opacity: 1 !important; transform: none !important; }
        }
      `}</style>
    </div>
  );
};
